// src/modules/clutchnotification/ClutchNotificationDashboard.jsx
import React, { useEffect, useState, useMemo } from "react";
import DaterangePicker from "../../common/Daterangepicker";
import { useSelector } from "react-redux";

// ==========================================
// 1. SHARED CONSTANTS & UTILITIES
// ==========================================
const formatCurrency = (amount) => {
  if (amount === undefined || amount === null || Number.isNaN(amount) || !Number.isFinite(amount)) return '₹0';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return dateString;
  }
};

// ==========================================
// 2. INFO TOOLTIP COMPONENT
// ==========================================
const InfoTooltip = ({ content, position = "top" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isVisible = isOpen || isHovered;

  const positionClasses = {
    top: "bottom-full left-1/2 transform -translate-x-1/2 mb-2",
    bottom: "top-full left-1/2 transform -translate-x-1/2 mt-2",
    left: "right-full top-1/2 transform -translate-y-1/2 mr-2",
    right: "left-full top-1/2 transform -translate-y-1/2 ml-2",
  };

  const arrowClasses = {
    top: "absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-slate-800 border-r border-b border-slate-700",
    bottom: "absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-slate-800 border-l border-t border-slate-700",
    left: "absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-slate-800 border-r border-t border-slate-700",
    right: "absolute left-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-slate-800 border-l border-b border-slate-700",
  };

  return (
    <div className="relative inline-flex items-center ml-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`
          inline-flex items-center justify-center 
          w-4 h-4 rounded-full 
          transition-all duration-200
          ${isVisible 
            ? 'bg-amber-500 text-white ring-2 ring-amber-400 ring-offset-1 scale-110' 
            : 'bg-gray-200 text-gray-600 hover:bg-amber-100 hover:text-amber-600 hover:ring-2 hover:ring-amber-200'
          }
          focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2
        `}
        aria-label="Information"
        aria-expanded={isVisible}
        type="button"
      >
        <i className="ti ti-info-circle text-[10px]" />
      </button>
      
      {isVisible && (
        <div 
          className={`absolute z-[9999] ${positionClasses[position]} w-64 bg-slate-800 text-white rounded-lg shadow-2xl p-3 text-xs pointer-events-auto animate-in fade-in slide-in-from-bottom-2 duration-200 border border-slate-700`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">{content}</div>
            <button 
              onClick={() => setIsOpen(false)}
              className="text-white/50 hover:text-white transition-colors p-0.5 flex-shrink-0"
              aria-label="Close tooltip"
              type="button"
            >
              <i className="ti ti-x text-sm" />
            </button>
          </div>
          <div className={arrowClasses[position]} />
        </div>
      )}
    </div>
  );
};

// ==========================================
// 3. STATISTICS CARDS COMPONENT
// ==========================================
const StatisticsCards = ({ stats, workTypeDistribution }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-5 mb-8">
      <div className="md:col-span-4 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
        <div className="flex items-center gap-2">
          <p className="text-sm opacity-80 flex items-center gap-2">
            <i className="ti ti-alert-triangle" /> Total Notifications
          </p>
          <InfoTooltip content="Total number of clutch notifications across all vehicles. Includes overdue and upcoming inspections." position="bottom" />
        </div>
        <div className="flex items-baseline gap-3 flex-wrap">
          <h2 className="text-4xl font-semibold mt-2 tracking-tight">
            {stats.totalNotifications}
          </h2>
        </div>
        <div className="mt-5 flex gap-6">
          <div>
            <p className="text-xs opacity-70">Overdue</p>
            <p className="text-xl font-medium">{stats.overdueCount}</p>
          </div>
          <div>
            <p className="text-xs opacity-70">Upcoming</p>
            <p className="text-xl font-medium">{stats.upcomingCount}</p>
          </div>
          <div>
            <p className="text-xs opacity-70">Unique Vehicles</p>
            <p className="text-xl font-medium">{stats.uniqueVehicles}</p>
          </div>
          <div>
            <p className="text-xs opacity-70">Critical Overdue</p>
            <p className="text-xl font-medium">{stats.criticalOverdue}</p>
          </div>
        </div>
      </div>
      
      {/* <div className="md:col-span-2 grid grid-cols-3 gap-4"> */}
        {/* <div className="bg-white rounded-xl p-4 border border-slate-200 border-l-4 border-l-red-500 shadow-sm flex flex-col justify-between">
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <span className="p-1.5 bg-red-50 text-red-600 rounded-md"><i className="ti ti-alert-circle" /></span>
            <span className="font-medium">Critical Overdue</span>
            <InfoTooltip content="Vehicles with overdue clutch inspections. Immediate action required." />
          </div>
          <p className="text-2xl font-semibold text-red-600 mt-2">{stats.criticalOverdue}</p>
        </div> */}
        
        {/* <div className="bg-white rounded-xl p-4 border border-slate-200 border-l-4 border-l-yellow-500 shadow-sm flex flex-col justify-between">
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <span className="p-1.5 bg-yellow-50 text-yellow-600 rounded-md"><i className="ti ti-clock" /></span>
            <span className="font-medium">Due Soon</span>
            <InfoTooltip content="Vehicles with upcoming clutch inspections due within the next 30 days." />
          </div>
          <p className="text-2xl font-semibold text-yellow-600 mt-2">{stats.dueSoon}</p>
        </div> */}
        
        {/* <div className="bg-white rounded-xl p-4 border border-slate-200 border-l-4 border-l-green-500 shadow-sm flex flex-col justify-between">
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <span className="p-1.5 bg-green-50 text-green-600 rounded-md"><i className="ti ti-check" /></span>
            <span className="font-medium">Ok</span>
            <InfoTooltip content="Vehicles with completed inspections and no overdue notifications." />
          </div>
          <p className="text-2xl font-semibold text-green-600 mt-2">{stats.compliant}</p>
        </div> */}
      {/* </div> */}

      <div className="md:col-span-8">
                  <WorkTypeChart workTypeDistribution={workTypeDistribution} />
      </div>
    </div>
  );
};

// ==========================================
// 4. WORK TYPE DISTRIBUTION CHART
// ==========================================
const WorkTypeChart = ({ workTypeDistribution }) => {
  const maxCount = Math.max(...workTypeDistribution.map(w => w.count), 1);
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm mb-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-slate-800 flex items-center gap-2 text-sm">
          <i className="ti ti-chart-bar text-amber-500" /> Work Type Distribution
          <InfoTooltip content="Distribution of notifications by work type. Shows which types of clutch work are most common." />
        </h3>
      </div>
      <div className="space-y-3">
        {workTypeDistribution.map((type) => {
          const barWidth = (type.count / maxCount) * 100;
          const percentage = maxCount > 0 ? ((type.count / maxCount) * 100).toFixed(1) : 0;
          
          return (
            <div key={type.name} className="space-y-1">
              <div className="flex justify-between text-xs">
                <span className="font-medium flex items-center gap-2 text-slate-700">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: type.color }}></span>
                  {type.name}
                </span>
                <span className="text-gray-500">
                  {type.count} notifications
                </span>
              </div>
              <div className="h-6 bg-gray-100 rounded-md overflow-hidden relative">
                <div 
                  className="h-full rounded-md transition-all duration-500 flex items-center px-2"
                  style={{ 
                    width: `${Math.max(barWidth, 4)}%`, 
                    backgroundColor: type.color,
                    opacity: 0.85
                  }}
                >
                  <span className="text-white text-[10px] font-bold">
                    {percentage}%
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==========================================
// 5. FILTERS BAR COMPONENT
// ==========================================
const FiltersBar = ({
  searchTerm, setSearchTerm,
  statusFilter, setStatusFilter,
  workTypeFilter, setWorkTypeFilter,
  sortBy, setSortBy,
  uniqueStatuses, uniqueWorkTypes,
  handleExportCSV, disableExport
}) => {
  const hasActiveFilters = statusFilter !== "all" || workTypeFilter !== "all" || searchTerm.trim() !== "";

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 shadow-sm space-y-4">
      <div className="w-full">
        <div className="relative">
          <label htmlFor="search-notifications" className="sr-only">Search by vehicle name or license plate</label>
          <i className="ti ti-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
          <input
            id="search-notifications"
            type="text"
            placeholder="Search by vehicle name or license plate..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-300"
          />
        </div>
      </div>
      
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative inline-flex items-center">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-200 pr-8"
              aria-label="Filter by notification status"
            >
              <option value="all">All statuses</option>
              {uniqueStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
            <InfoTooltip content="Filter notifications by their current status: OVERDUE, UPCOMING, or COMPLETED." position="bottom" />
          </div>
          
          <div className="relative inline-flex items-center">
            <select
              value={workTypeFilter}
              onChange={(e) => setWorkTypeFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-200 pr-8"
              aria-label="Filter by work type"
            >
              <option value="all">All work types</option>
              {uniqueWorkTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <InfoTooltip content="Filter notifications by the type of clutch work: Inspection, Adjustment, or Replacement." position="bottom" />
          </div>
          
          <div className="relative inline-flex items-center">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-amber-200 pr-8"
              aria-label="Sort notifications"
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="critical">Most critical</option>
            </select>
            <InfoTooltip content="Sort notifications by date or criticality. Critical sorting shows overdue items first." position="bottom" />
          </div>
        </div>

        <div className="relative inline-flex items-center">
          <button
            onClick={handleExportCSV}
            disabled={disableExport}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-all ${
              disableExport ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
            }`}
          >
            <i className="ti ti-download text-sm" />
            Export CSV
          </button>
          <InfoTooltip content="Download the current filtered data as a CSV file. All active filters will be applied to the export." position="left" />
        </div>
      </div>
      
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100">
          <span className="text-xs text-gray-400 mr-1">Active filters:</span>
          {statusFilter !== "all" && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-medium border border-slate-200">
              Status: {statusFilter}
              <button onClick={() => setStatusFilter("all")} className="ml-1 text-slate-400 hover:text-amber-600 focus:outline-none font-bold" aria-label="Remove status filter">×</button>
            </span>
          )}
          {workTypeFilter !== "all" && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-medium border border-slate-200">
              Work Type: {workTypeFilter}
              <button onClick={() => setWorkTypeFilter("all")} className="ml-1 text-slate-400 hover:text-amber-600 focus:outline-none font-bold" aria-label="Remove work type filter">×</button>
            </span>
          )}
          {searchTerm.trim() !== "" && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-medium border border-slate-200">
              Search: "{searchTerm}"
              <button onClick={() => setSearchTerm("")} className="ml-1 text-slate-400 hover:text-amber-600 focus:outline-none font-bold" aria-label="Remove search term">×</button>
            </span>
          )}
          <button 
            onClick={() => { setStatusFilter("all"); setWorkTypeFilter("all"); setSearchTerm(""); }}
            className="text-xs text-amber-500 hover:text-amber-600 font-medium ml-auto"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};

// ==========================================
// 6. NOTIFICATIONS TABLE
// ==========================================
const NotificationsTable = ({ notifications, expandedRowId, toggleRowExpansion, onVehicleClick }) => {
  return (
    <div className="divide-y divide-slate-100">
      {notifications.map((notification, index) => {
        const isExpanded = expandedRowId === notification.vehicleId + notification.work_type;
        const kmUntilInspection = parseInt(notification.km_until_inspection) || 0;
        const isOverdue = notification.notification_status === 'OVERDUE';
        const isUpcoming = notification.notification_status === 'UPCOMING';
        
        return (
           <React.Fragment key={`${notification.vehicleId}-${notification.work_type}-${index}`}>
            <div 
              className={`px-5 py-4 hover:bg-slate-50 transition-all cursor-pointer outline-none focus:bg-slate-50 ${isExpanded ? 'bg-slate-50 font-medium' : ''}`}
              onClick={() => toggleRowExpansion(notification.vehicleId + notification.work_type)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleRowExpansion(notification.vehicleId + notification.work_type);
                }
              }}
              role="button"
              tabIndex={0}
              aria-expanded={isExpanded}
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} details for ${notification.name}`}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span 
                      className="font-bold text-slate-800 text-sm tracking-tight hover:text-amber-600 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        onVehicleClick(notification);
                      }}
                    >
                      {notification.name || 'N/A'}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                      isOverdue ? 'bg-red-100 text-red-700' :
                      isUpcoming ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {notification.notification_status || 'N/A'}
                    </span>
                    <span className="text-xs text-gray-400">
                      {notification.license_plate || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400 mt-1 font-normal">
                    <span>Vehicle ID: {notification.vehicleId || 'N/A'}</span>
                    <span className="w-1 h-1 rounded-full bg-gray-300" />
                    <span>Work Type: {notification.work_type || 'N/A'}</span>
                    <span className="w-1 h-1 rounded-full bg-gray-300" />
                    <span>Current Odometer: {notification.current_odometer || 0} km</span>
                  </div>
                </div>
                
                <div className="hidden md:block flex-shrink-0 text-xs text-gray-400 font-normal">
                  <div>Service Date: {formatDateTime(notification.service_date)}</div>
                  {notification.next_inspection_odo > 0 && (
                    <div className="text-[10px]">Next ODO: {notification.next_inspection_odo} km</div>
                  )}
                </div>
                
                <div className="flex-shrink-0 text-right">
                  <div className={`text-lg font-bold tracking-tight ${
                    isOverdue ? 'text-red-600' :
                    isUpcoming ? 'text-yellow-600' :
                    'text-green-600'
                  }`}>
                    {isOverdue ? `${Math.abs(kmUntilInspection)} km overdue` :
                     isUpcoming ? `${kmUntilInspection} km remaining` :
                     'Completed'}
                  </div>
                  {notification.next_change_predicted_odo && (
                    <div className="text-[10px] text-gray-400">
                      Predicted: {notification.next_change_predicted_odo} km
                    </div>
                  )}
                </div>
                
                <div className="flex-shrink-0 text-gray-400">
                  <i className={`ti ${isExpanded ? 'ti-chevron-up' : 'ti-chevron-down'} text-sm`} />
                </div>
              </div>
            </div>
            
            {isExpanded && (
              <div className="px-5 py-5 bg-slate-50 border-t border-b border-slate-100 animate-in fade-in duration-200">
                <div className="grid grid-cols-12 gap-5">
                  <div className="col-span-12 md:col-span-6">
                    <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-xs">
                      <h4 className="font-semibold text-slate-700 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                        <i className="ti ti-car text-gray-400" /> Vehicle Details
                        <InfoTooltip content="Detailed information about the vehicle including license plate, current odometer reading, and service history." />
                      </h4>
                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Vehicle ID</span>
                          <span className="font-medium text-slate-700">{notification.vehicleId || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">License Plate</span>
                          <span className="font-medium text-slate-700">{notification.license_plate || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Vehicle Name</span>
                          <span className="font-medium text-slate-700">{notification.name || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Current Odometer</span>
                          <span className="font-medium text-slate-700">{notification.current_odometer || 0} km</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Service Date</span>
                          <span className="font-medium text-slate-700">{formatDateTime(notification.service_date)}</span>
                        </div>
                        <div className="flex justify-between py-1">
                          <span className="text-gray-500">Work Type</span>
                          <span className="font-medium text-slate-700">{notification.work_type || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-span-12 md:col-span-6">
                    <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-xs">
                      <h4 className="font-semibold text-slate-700 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                        <i className="ti ti-chart-arrows text-gray-400" /> Inspection Details
                        <InfoTooltip content="Inspection and maintenance details including next inspection odometer and predicted change odometer." />
                      </h4>
                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Next Inspection ODO</span>
                          <span className="font-medium text-slate-700">{notification.next_inspection_odo || 'N/A'} km</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Next Change Predicted ODO</span>
                          <span className="font-medium text-slate-700">{notification.next_change_predicted_odo || 'N/A'} km</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">KM Until Inspection</span>
                          <span className={`font-medium ${isOverdue ? 'text-red-600' : isUpcoming ? 'text-yellow-600' : 'text-green-600'}`}>
                            {notification.km_until_inspection || 0} km
                          </span>
                        </div>
                        <div className="flex justify-between py-2 border-t-2 border-slate-200">
                          <span className="text-gray-500 font-medium">Status</span>
                          <span className={`font-bold ${
                            isOverdue ? 'text-red-600' :
                            isUpcoming ? 'text-yellow-600' :
                            'text-green-600'
                          }`}>
                            {notification.notification_status || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ==========================================
// 7. MAIN DASHBOARD COMPONENT
// ==========================================
const ClutchNotificationDashboard = () => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");
  const cities = useSelector((state) => state?.cities?.citiesData);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [apiError, setApiError] = useState(false);
  const [getAllHubsListData, setGetAllHubsListData] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [workTypeFilter, setWorkTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("critical");
  const [toast, setToast] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const today = new Date();
    today.setHours(23, 59, 59, 999);
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(today.getMonth() - 1);
    const [selectionRange, setSelectionRange] = useState({
      startDate: oneMonthAgo,
      endDate: today,
      key: "selection",
    });
    const [cityId, setCityId] = useState("");
      const [hubId, setHubId] = useState("");

  const [visibleCount, setVisibleCount] = useState(10);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const getAllHubsList = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/hubs?limit=1000`);
      const data = await response.json();
      if (data?.statusCode === "0100")
        setGetAllHubsListData(data?.entities?.rows);
    } catch (error) {
      console.error("Error fetching City Names:", error);
    }
  };

  function formatDateForApi(date, type) {
    if (!date) return "";
    const pad = (n) => (n < 10 ? "0" + n : n);
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    let hours = type === "start" ? "00" : "23";
    let minutes = type === "start" ? "00" : "59";
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  // Fetch clutch notification data
  const fetchClutchData = async (cId = cityId, hId = hubId) => {
    try {
      setLoading(true);
      setApiError(false);

       const formattedStartDate = formatDateForApi(selectionRange?.startDate);
      const formattedEndDate = formatDateForApi(selectionRange?.endDate);
      const queryParts = [];
      if (cId) queryParts.push(`cityId=${cId}`);
      if (hId) queryParts.push(`hubId=${hId}`);
      const queryString = queryParts.length ? `&${queryParts.join("&")}` : "";

      
      const response = await fetch(
        `${apiUrl}/api/dashboardreports/clutch-notification-dashboard?${queryString}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `${token}`,
          },
        }
      );
      
      const data = await response.json();
      
      if (data?.statusCode === "0100") {
        const notificationsData = data?.dataValues || [];
        setNotifications(notificationsData);
      } else {
        setNotifications([]);
      }
      
      setLastUpdated(new Date());
      
    } catch (error) {
      console.error("Error fetching clutch data:", error);
      setNotifications([]);
      setApiError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClutchData();
    getAllHubsList()
  }, []);

  // Handler for vehicle click
  const handleVehicleClick = (notification) => {
    // window.location.href = `/vehiclesdashboard?vehicleid=${notification.vehicleId}&display=true`;
    window.open(`/vehiclesdashboard?vehicleid=${notification.vehicleId}&display=true`,"_blank")
  };

  // Extract unique statuses and work types
  const uniqueStatuses = useMemo(() => {
    return Array.from(new Set(notifications.map(n => n.notification_status).filter(Boolean)));
  }, [notifications]);

  const uniqueWorkTypes = useMemo(() => {
    return Array.from(new Set(notifications.map(n => n.work_type).filter(Boolean)));
  }, [notifications]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalNotifications = notifications.length;
    const overdueCount = notifications.filter(n => n.notification_status === 'OVERDUE').length;
    const upcomingCount = notifications.filter(n => n.notification_status === 'UPCOMING').length;
    const completedCount = notifications.filter(n => n.notification_status === 'COMPLETED').length;
    const uniqueVehicles = new Set(notifications.map(n => n.vehicleId)).size;
    
    // Critical overdue: KM until inspection is negative and less than -500
    const criticalOverdue = notifications.filter(n => {
      const km = parseInt(n.km_until_inspection) || 0;
      return n.notification_status === 'OVERDUE' && km < -500;
    }).length;
    
    // FIXED: Due soon - Check both kilometers AND days remaining
    const dueSoon = notifications.filter(n => {
      const km = parseInt(n.km_until_inspection) || 0;
      
      // Check if it's UPCOMING
      if (n.notification_status !== 'UPCOMING') return false;
      
      // Check kilometer-based due soon (less than 500 km remaining)
      const kmBased = km < 500 && km >= 0;
      
      // Check date-based due soon (within 30 days from service_date)
      let dateBased = false;
      if (n.service_date) {
        const serviceDate = new Date(n.service_date);
        const today = new Date();
        const daysDiff = Math.ceil((serviceDate - today) / (1000 * 60 * 60 * 24));
        dateBased = daysDiff >= 0 && daysDiff <= 30;
      }
      
      // A notification is "due soon" if either condition is met
      return kmBased || dateBased;
    }).length;
    
    // FIXED: Compliant - Vehicles with completed inspections AND no overdue notifications
    // Get all vehicle IDs that have active (overdue or upcoming) notifications
    const activeVehicleIds = new Set(
      notifications
        .filter(n => n.notification_status === 'OVERDUE' || n.notification_status === 'UPCOMING')
        .map(n => n.vehicleId)
    );
    
    // Get all vehicle IDs that have completed notifications
    const completedVehicleIds = new Set(
      notifications
        .filter(n => n.notification_status === 'COMPLETED')
        .map(n => n.vehicleId)
    );
    
    // Compliant vehicles = those with completed notifications AND no active notifications
    const compliant = Array.from(completedVehicleIds).filter(
      vehicleId => !activeVehicleIds.has(vehicleId)
    ).length;
    
    return {
      totalNotifications,
      overdueCount,
      upcomingCount,
      completedCount,
      uniqueVehicles,
      criticalOverdue,
      dueSoon,
      compliant
    };
  }, [notifications]);

  // Work type distribution
  const workTypeDistribution = useMemo(() => {
    const distribution = {};
    const colorMap = {
      'Inspection': '#3b82f6',
      'Adjustment': '#f59e0b',
      'Replacement': '#ef4444'
    };
    
    notifications.forEach(n => {
      const type = n.work_type || 'Unknown';
      if (!distribution[type]) distribution[type] = 0;
      distribution[type]++;
    });
    
    return Object.entries(distribution).map(([name, count]) => ({
      name,
      count,
      color: colorMap[name] || '#64748b'
    }));
  }, [notifications]);

  // Filtered notifications
  const filteredNotifications = useMemo(() => {
    let result = [...notifications];
    
    if (statusFilter !== "all") {
      result = result.filter(n => n.notification_status === statusFilter);
    }
    
    if (workTypeFilter !== "all") {
      result = result.filter(n => n.work_type === workTypeFilter);
    }
    
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(n => 
        (n.name || '').toLowerCase().includes(term) ||
        (n.license_plate || '').toLowerCase().includes(term) ||
        (n.work_type || '').toLowerCase().includes(term) ||
        String(n.vehicleId).includes(term)
      );
    }
    
    if (sortBy === "critical") {
      result.sort((a, b) => {
        const kmA = parseInt(a.km_until_inspection) || 0;
        const kmB = parseInt(b.km_until_inspection) || 0;
        return kmA - kmB;
      });
    } else if (sortBy === "newest") {    
      result.sort((a, b) => {       
        // Handle null/undefined values
        if (!a.service_date && !b.service_date) return 0;
        if (!a.service_date) return 1;
        if (!b.service_date) return -1;
        
        const dateA = new Date(a.service_date);
        const dateB = new Date(b.service_date);
        
        // Handle invalid dates
        if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
        if (isNaN(dateA.getTime())) return 1;
        if (isNaN(dateB.getTime())) return -1;
        
        const result = dateB - dateA;
        return result;
      });
    } else if (sortBy === "oldest") {
      result.sort((a, b) => {
        if (!a.service_date && !b.service_date) return 0;
        if (!a.service_date) return 1;
        if (!b.service_date) return -1;
        
        const dateA = new Date(a.service_date);
        const dateB = new Date(b.service_date);
        
        if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
        if (isNaN(dateA.getTime())) return 1;
        if (isNaN(dateB.getTime())) return -1;
        
        return dateA - dateB;
      });
    }
    
    return result;
  }, [notifications, statusFilter, workTypeFilter, searchTerm, sortBy]);

  // Export CSV
  const handleExportCSV = () => {
    const escapeCSV = (val) => {
      if (val === undefined || val === null) return '""';
      const cleanStr = String(val).replace(/"/g, '""');
      return cleanStr.includes(',') || cleanStr.includes('"') || cleanStr.includes('\n') ? `"${cleanStr}"` : cleanStr;
    };

    const headers = [
      'Vehicle ID', 'License Plate', 'Vehicle Name', 'Service Date', 
      'Work Type', 'Next Inspection ODO', 'Next Change Predicted ODO',
      'Current Odometer', 'KM Until Inspection', 'Notification Status'
    ];
    
    const rows = filteredNotifications.map(n => [
      n.vehicleId || '', n.license_plate || '', n.name || '',
      formatDateTime(n.service_date), n.work_type || '',
      n.next_inspection_odo || 0, n.next_change_predicted_odo || 'N/A',
      n.current_odometer || 0, n.km_until_inspection || 0,
      n.notification_status || ''
    ]);

    const csvContent = [headers, ...rows].map(r => r.map(escapeCSV).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `Clutch_Notifications_Export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showToast('CSV exported successfully!', 'success');
  };

  const SkeletonState = () => (
    <div className="space-y-6 animate-pulse">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
        <div className="md:col-span-4 h-36 bg-slate-200 rounded-2xl" />
        <div className="md:col-span-8 grid grid-cols-3 gap-4">
          <div className="bg-slate-200 rounded-xl h-36" />
          <div className="bg-slate-200 rounded-xl h-36" />
          <div className="bg-slate-200 rounded-xl h-36" />
        </div>
      </div>
      <div className="h-44 bg-slate-200 rounded-xl" />
      <div className="h-64 bg-slate-200 rounded-xl" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 p-6 font-sans text-slate-800 my-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            Clutch Notification Dashboard
            <InfoTooltip content="Dashboard for monitoring vehicle clutch notifications and inspection schedules. Tracks overdue, upcoming, and completed clutch services." position="bottom" />
          </h1>
          <p className="text-sm text-gray-500 mt-1">Monitor vehicle clutch notifications and inspection schedules.</p>
        </div>
        <div className="flex bg-white rounded-xl border border-slate-200 p-4 mb-6 shadow-sm gap-4 animate-in slide-in-from-top-4 duration-200">
          {/* <DaterangePicker
            selectionRange={selectionRange}
            setSelectionRange={setSelectionRange}
            style={-10}
          /> */}
          <select
            className="w-40 h-11 px-4 pr-10 border border-gray-300 rounded-lg bg-white text-gray-700 shadow-sm outline-none appearance-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
            value={cityId}
            onChange={(e) => setCityId(e.target.value)}
          >
            <option>Select City</option>
            {cities &&
              Object.entries(cities).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
          </select>
          <select
            className="w-40 h-11 px-4 pr-10 border border-gray-300 rounded-lg bg-white text-gray-700 shadow-sm outline-none appearance-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
            value={hubId}
            onChange={(e) => setHubId(e.target.value)}
          >
            <option>Select Hub</option>
            {getAllHubsListData &&
              getAllHubsListData.map((item, index) => (
                <option key={index} value={item?.id}>
                  {item?.hubName}
                </option>
              ))}
          </select>

          <button
            onClick={() => {
              fetchClutchData();
            }}
            className="p-2 bg-blue-400"
          >
            Search
          </button>
          <button
            onClick={() => {
              setCityId("");
              setHubId("");
              const resetRange = {
                startDate: oneMonthAgo,
                endDate: today,
                key: "selection",
              };
              setSelectionRange(resetRange);
              fetchClutchData("", "");
            }}
            className="p-2 bg-red-400"
          >
            Clear
          </button>
        </div>
      </div>

      {apiError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-6 flex items-center justify-between shadow-xs">
          <p className="text-red-700 text-xs font-medium flex items-center gap-2">
            <i className="ti ti-alert-triangle text-sm" /> Failed to fetch clutch notification data. Please check your connection and try again.
          </p>
          <button onClick={fetchClutchData} className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-xs font-bold transition-all">
            Retry
          </button>
        </div>
      )}

      {loading ? <SkeletonState /> : (
        <>
          <StatisticsCards stats={stats} workTypeDistribution={workTypeDistribution} />
                    
          <FiltersBar 
            searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            statusFilter={statusFilter} setStatusFilter={setStatusFilter}
            workTypeFilter={workTypeFilter} setWorkTypeFilter={setWorkTypeFilter}
            sortBy={sortBy} setSortBy={setSortBy}
            uniqueStatuses={uniqueStatuses} uniqueWorkTypes={uniqueWorkTypes}
            handleExportCSV={handleExportCSV} disableExport={filteredNotifications.length === 0}
          />

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex justify-center items-center">
              <span className="text-xs font-medium text-gray-500">
                Showing {filteredNotifications.length} of {notifications.length} notifications
              </span>
              <InfoTooltip content="Click on any row to expand and view detailed information about the vehicle and inspection details." position="bottom" />
            </div>
            {filteredNotifications.length === 0 ? (
              <div className="py-16 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-slate-50 rounded-full mb-3 border border-slate-100">
                  <i className="ti ti-check text-slate-400 text-xl" />
                </div>
                <h3 className="text-sm font-bold text-slate-700 mb-0.5">No notifications found</h3>
                <p className="text-xs text-gray-400">Adjust your filters to see more results.</p>
              </div>
            ) : (
              <>
                <NotificationsTable 
                  notifications={filteredNotifications.slice(0, visibleCount)}
                  expandedRowId={expandedRowId}
                  toggleRowExpansion={(id) => setExpandedRowId(expandedRowId === id ? null : id)}
                  onVehicleClick={handleVehicleClick}
                />
                
                {filteredNotifications.length > visibleCount && (
                  <div className="p-3 bg-slate-50/50 text-center border-t border-slate-100">
                    <button 
                      onClick={() => setVisibleCount(prev => prev + 10)}
                      className="px-4 py-1.5 bg-white hover:bg-slate-50 text-slate-700 font-semibold border border-slate-200 rounded-lg text-xs shadow-xs transition-colors"
                    >
                      Load more ({filteredNotifications.length - visibleCount} remaining)
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-5 fade-in duration-300">
          <div className="rounded-xl bg-slate-900 text-white font-medium shadow-xl px-4 py-3 flex items-center gap-2.5 text-xs border border-slate-800">
            <i className="ti ti-circle-check text-emerald-400 text-sm" />
            <span>{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClutchNotificationDashboard;